import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Box } from "@mui/material";
import AppSettings from "app/components/flow/designer/appSettings";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import statusManagement from "src/status-management";
import { sxStyles } from "../component-form/sidebar.styles";

interface AppSettingsWrapperProps {
    app: any;
    appStatus?: string;
    deployment?: boolean;
    onClose?: () => void;
}

interface AppSettingsWrapperRef {
    isDirty: () => boolean;
}

const AppSettingsWrapper = forwardRef<AppSettingsWrapperRef, AppSettingsWrapperProps>(
    ({ app, appStatus, deployment = false, onClose }, ref) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const viewRef = useRef<any>(null);

        // Expose isDirty method to parent component
        useImperativeHandle(ref, () => ({
            isDirty: () => {
                return viewRef.current && typeof viewRef.current.isDirty === "function" && viewRef.current.isDirty();
            }
        }));

        useEffect(() => {
            if (!containerRef.current || !app) {
                return;
            }

            const loadAndShowAppSettings = async () => {
                try {
                    // Ensure we have the latest deployment info from server
                    // This matches the logic from the original designer.js
                    const latestApp = await metaStoreService.findById(app.id);
                    app.deploymentInfo = latestApp.deploymentInfo;

                    // Determine if the settings should be read-only
                    const currentAppStatus = appStatus || app.flowStatus;
                    const readOnly =
                        statusManagement.isApplicationRunning(currentAppStatus) ||
                        statusManagement.isDeployedState(currentAppStatus) ||
                        statusManagement.isApplicationCrash(currentAppStatus);

                    // Create the AppSettings view
                    const appSettingsView = new AppSettings.View({
                        model: app,
                        deployment: deployment,
                        readOnly: readOnly
                    });

                    // Listen to the cancel event to close the sidebar
                    appSettingsView.on("appSettings-cancel", () => {
                        if (onClose) {
                            onClose();
                        }
                    });

                    // Render the view into the container
                    const renderedEl = appSettingsView.render().el;
                    containerRef.current!.appendChild(renderedEl);
                    appSettingsView.triggerMethod("show");

                    // Store the view reference for cleanup
                    viewRef.current = appSettingsView;
                } catch (error) {
                    console.error("Failed to load app settings:", error);
                    if (onClose) {
                        onClose();
                    }
                }
            };

            loadAndShowAppSettings();

            // Cleanup function
            return () => {
                if (viewRef.current) {
                    viewRef.current.destroy();
                    viewRef.current = null;
                }
            };
        }, [app, appStatus, deployment, onClose]);

        return <Box ref={containerRef} sx={sxStyles.appSettings} />;
    }
);

export default AppSettingsWrapper;
